// Candidates for an "@" mention. General chat uses the same bounded org
// searches as the established chat picker. Outreach chat leads with the
// outreach's own shifts and people, which is what a coordinator there
// usually tags, then offers the org-wide searches underneath: caregivers,
// clients, and last the shifts, for the occasional shift to add.
import { useCallback, useMemo } from 'react';

import {
    buildOutreachMentionCandidates,
    type MentionCandidate,
    personCandidate,
    type SearchableMentionCandidate,
    shiftCandidate,
} from '@apps/web/features/phoebe_chat_v3/model/mention_candidates';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    dateKeyToStartUtcIso,
    shiftDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { type ApiResponseType } from '@libraries/typescript/api_client';

export { type MentionCandidate };

const REASON = 'v3-chat-mention-picker';
const MAX_PER_TYPE = 6;
/** The outreach section lists every group but only a first page of shifts:
 * a 21-shift outreach would otherwise fill the picker on its own. */
const OUTREACH_SHIFT_LIMIT = 8;
// Org-wide shift search needs a prefix to match on; the other sections wait
// for the same prefix so an opened picker shows the outreach alone.
const OTHER_SECTION_MIN_QUERY = 2;
// The bounded client search rejects one- and two-character terms; an empty
// term lists the first few clients, which the general chat shows on "@".
const CLIENT_SEARCH_MIN_QUERY = 3;
const SHIFT_WINDOW_DAYS_BACK = 7;
const SHIFT_WINDOW_DAYS_FORWARD = 14;
const NO_CANDIDATES: ReadonlyArray<MentionCandidate> = [];
const NO_SEARCHABLE: ReadonlyArray<SearchableMentionCandidate> = [];

const OUTREACH_LIMIT_BY_TYPE: Readonly<
    Record<MentionCandidate['type'], number>
> = {
    group: Number.POSITIVE_INFINITY,
    shift: OUTREACH_SHIFT_LIMIT,
    client: MAX_PER_TYPE,
    caregiver: MAX_PER_TYPE,
};

type CaregiverListResponse = ApiResponseType<
    '/organizations/{organization_id}/caregivers',
    'get'
>;
type ClientSearchResponse = ApiResponseType<
    '/organizations/{organization_id}/clients/search',
    'get'
>;
type ShiftSearchResponse = ApiResponseType<
    '/organizations/{organization_id}/shifts/search',
    'get'
>;

function matchesQuery(searchText: string, query: string): boolean {
    const terms = query.toLowerCase().split(/\s+/u).filter(Boolean);
    const searchable = searchText.toLowerCase();
    return terms.every((term) => searchable.includes(term));
}

export function useReferenceSearch({
    enabled,
    mode,
    organizationId,
    organizationTimeZone,
    outreachId,
    query,
}: Readonly<{
    readonly enabled: boolean;
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    readonly organizationTimeZone: string;
    readonly outreachId?: string;
    readonly query: string;
}>): Readonly<{
    readonly candidates: ReadonlyArray<MentionCandidate>;
    /** The outreach has more shifts matching this query than its section
     * shows, so the picker can offer filtering instead of guessing. */
    readonly outreachShiftsTruncated: boolean;
    readonly hasRequestFailed: boolean;
    readonly isLoading: boolean;
}> {
    const { formatName } = useFormatPersonName();
    // Memoized select functions: `useApiQuery` re-runs `select` whenever its
    // identity changes, and a fresh candidate array each render would feed
    // the composer's results effect forever.
    const selectCaregiverCandidates = useCallback(
        (response: CaregiverListResponse): ReadonlyArray<MentionCandidate> =>
            response.caregivers.map((caregiver) =>
                personCandidate('caregiver', caregiver, 'other', formatName),
            ),
        [formatName],
    );
    const selectClientCandidates = useCallback(
        (response: ClientSearchResponse): ReadonlyArray<MentionCandidate> =>
            response.clients.map((client) =>
                personCandidate(
                    'client',
                    { id: client.id, name: client.name },
                    'other',
                    formatName,
                ),
            ),
        [formatName],
    );
    const trimmed = query.trim();
    const inOutreach = outreachId !== undefined;
    const otherSectionEnabled =
        enabled && (!inOutreach || trimmed.length >= OTHER_SECTION_MIN_QUERY);
    const otherShiftsEnabled =
        otherSectionEnabled && trimmed.length >= OTHER_SECTION_MIN_QUERY;
    const clientsEnabled =
        otherSectionEnabled &&
        (trimmed.length === 0 || trimmed.length >= CLIENT_SEARCH_MIN_QUERY);
    const caregivers = useApiQuery({
        path: '/organizations/{organization_id}/caregivers',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: { search: trimmed, per_page: MAX_PER_TYPE },
        mode,
        enabled: otherSectionEnabled,
        reason: REASON,
        select: selectCaregiverCandidates,
    });
    const clients = useApiQuery({
        path: '/organizations/{organization_id}/clients/search',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: { search: trimmed, limit: MAX_PER_TYPE },
        mode,
        enabled: clientsEnabled,
        reason: REASON,
        select: selectClientCandidates,
    });
    const shiftWindow = useMemo(() => {
        const today = toDateKey(new Date(), organizationTimeZone);
        return {
            shift_start_date: dateKeyToStartUtcIso(
                shiftDateKey(today, -SHIFT_WINDOW_DAYS_BACK),
                organizationTimeZone,
            ),
            shift_end_date: dateKeyToStartUtcIso(
                shiftDateKey(today, SHIFT_WINDOW_DAYS_FORWARD + 1),
                organizationTimeZone,
            ),
        };
    }, [organizationTimeZone]);
    const selectShiftCandidates = useCallback(
        (response: ShiftSearchResponse): ReadonlyArray<MentionCandidate> =>
            response.shifts
                .filter((shift) => shift.status !== 'missing')
                .map(
                    (shift) =>
                        shiftCandidate(
                            shift,
                            organizationTimeZone,
                            'other',
                            null,
                            formatName,
                        ).candidate,
                ),
        [organizationTimeZone, formatName],
    );
    const shifts = useApiQuery({
        path: '/organizations/{organization_id}/shifts/search',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: {
            search: trimmed,
            limit: MAX_PER_TYPE,
            ...shiftWindow,
        },
        mode,
        enabled: otherShiftsEnabled,
        reason: REASON,
        select: selectShiftCandidates,
    });
    const outreach = useApiQuery({
        path: '/organizations/{organization_id}/outreaches/joined/{outreach_id}',
        method: 'get',
        pathParams: {
            organization_id: organizationId,
            outreach_id: outreachId ?? '',
        },
        mode,
        enabled: enabled && inOutreach,
        reason: REASON,
        select: (response) => response,
    });
    const caregiverCandidates = caregivers.data ?? NO_CANDIDATES;
    const clientCandidates = clients.data ?? NO_CANDIDATES;
    const shiftCandidates = shifts.data ?? NO_CANDIDATES;
    const outreachCandidates = useMemo(
        () =>
            outreach.data === undefined
                ? NO_SEARCHABLE
                : buildOutreachMentionCandidates(
                      outreach.data,
                      organizationTimeZone,
                      formatName,
                  ),
        [organizationTimeZone, outreach.data, formatName],
    );
    const outreachSearch = useMemo(() => {
        const outreachSection: Array<MentionCandidate> = [];
        const countByType = new Map<MentionCandidate['type'], number>();
        let shiftsTruncated = false;
        for (const { candidate, searchText } of outreachCandidates) {
            if (!matchesQuery(searchText, trimmed)) {
                continue;
            }
            const count = countByType.get(candidate.type) ?? 0;
            if (count >= OUTREACH_LIMIT_BY_TYPE[candidate.type]) {
                shiftsTruncated ||= candidate.type === 'shift';
                continue;
            }
            countByType.set(candidate.type, count + 1);
            outreachSection.push(candidate);
        }
        // Only rows the outreach section shows are withheld below it; a
        // shift the cap hid stays reachable through the org-wide search.
        const scoped = new Set(
            outreachSection.map(
                (candidate) => `${candidate.type}:${candidate.id}`,
            ),
        );
        function unscoped(
            section: ReadonlyArray<MentionCandidate>,
        ): ReadonlyArray<MentionCandidate> {
            return section.filter(
                (candidate) => !scoped.has(`${candidate.type}:${candidate.id}`),
            );
        }
        const otherSections = inOutreach
            ? [caregiverCandidates, clientCandidates, shiftCandidates]
            : [shiftCandidates, clientCandidates, caregiverCandidates];
        return {
            candidates: [
                ...outreachSection,
                ...otherSections.flatMap((section) => unscoped(section)),
            ],
            shiftsTruncated,
        };
    }, [
        caregiverCandidates,
        clientCandidates,
        inOutreach,
        outreachCandidates,
        shiftCandidates,
        trimmed,
    ]);
    const otherFailed =
        (otherSectionEnabled && caregivers.hasRequestFailed) ||
        (clientsEnabled && clients.hasRequestFailed) ||
        (otherShiftsEnabled && shifts.hasRequestFailed);
    const otherLoading =
        (otherSectionEnabled && caregivers.isLoading) ||
        (clientsEnabled && clients.isLoading) ||
        (otherShiftsEnabled && shifts.isLoading);
    return {
        candidates: outreachSearch.candidates,
        outreachShiftsTruncated: outreachSearch.shiftsTruncated,
        hasRequestFailed:
            (inOutreach && outreach.hasRequestFailed) || otherFailed,
        isLoading: (inOutreach && outreach.isLoading) || otherLoading,
    };
}
